// CommunicatorPlugin/client/ui/Softphone.js
import { ComponentBase } from "../core/ComponentBase.js";

export class Softphone extends ComponentBase {
    static styles = `
        .softphone-container {
            width: 100%;
            max-width: 350px;
            min-width: 300px;
            position: fixed;
            top: 20px;
            right: 20px;
            z-index: 1050;
            font-family: var(--bs-font-sans-serif);
            font-size: 0.875rem;
            box-shadow: var(--bs-box-shadow-lg);
            border-radius: var(--bs-border-radius-lg);
            background: var(--bs-body-bg);
            border: 1px solid var(--bs-border-color);
            color: var(--bs-body-color);
            transition: opacity 0.3s ease, transform 0.3s ease;
        }
        .softphone-container.hidden {
            opacity: 0;
            transform: translateY(20px);
            pointer-events: none;
        }
        .softphone-header {
            background: var(--bs-tertiary-bg);
            border-bottom: 1px solid var(--bs-border-color);
            padding: 0.75rem;
            border-radius: var(--bs-border-radius-lg) var(--bs-border-radius-lg) 0 0;
            display: flex;
            justify-content: space-between;
            align-items: center;
            cursor: move;
            user-select: none;
        }
        .status-indicator {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: var(--bs-danger);
            display: inline-block;
            margin-right: 0.5rem;
            animation: pulse 2s infinite;
        }
        .status-indicator.online {
            background: var(--bs-success);
            animation: none;
        }
        .status-indicator.registering {
            background: var(--bs-warning);
        }
        .softphone-body {
            padding: 1rem;
            position: relative;
            min-height:75px;
        }
        .disabled-overlay {
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            background: rgba(var(--bs-body-bg-rgb), 0.95);
            border-radius: 0 0 var(--bs-border-radius-lg) var(--bs-border-radius-lg);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10;
        }
        .call-item {
            background: var(--bs-secondary-bg);
            border: 1px solid var(--bs-border-color);
            border-radius: var(--bs-border-radius);
            padding: 0.75rem;
            margin-bottom: 0.5rem;
            cursor: pointer;
            transition: all 0.2s ease;
        }
        .call-item:hover {
            background: var(--bs-tertiary-bg);
            border-color: var(--bs-primary);
        }
        .call-item.active {
            background: var(--bs-primary-bg-subtle);
            border-color: var(--bs-primary);
        }
        .call-duration {
            font-family: monospace;
            font-weight: bold;
            color: var(--bs-success);
        }
        .btn-control.active {
            background-color: var(--bs-warning);
            border-color: var(--bs-warning);
            color: var(--bs-dark);
        }
        .phone-input {
            font-size: 1.1rem;
            height: calc(2.5rem + 2px);
        }
        .dtmf-btn {
            aspect-ratio: 1;
            font-size: 1.1rem;
            font-weight: bold;
        }
        @keyframes pulse {
            0% { opacity: 1; } 50% { opacity: 0.5; } 100% { opacity: 1; }
        }
        .softphone-container.dragging {
            opacity: 0.8;
        }
    `;

    constructor(config) {
        super(config);
        this.instanceId = `softphone_${this.config.instanceId || Math.random().toString(36).substr(2, 9)}`;

        this.userAgent = null;
        this.registerer = null;
        this.activeCalls = new Map();
        this.currentCallId = null;

        // --- FIX: Start enabled but HIDDEN by default ---
        this.isRegistered = false;
        this.isDestroyed = false;
        this.isDialing = false;
        this.timerInterval = null;
        this.isEnabled = false;
        this.isHidden = true; // NEW STATE: Start completely hidden
        // --- END FIX ---

        this.reconnectInterval = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 10;
        this.eventListeners = {};

        this._boundHandleMouseMove = this.handleMouseMove.bind(this);
        this._boundHandleMouseUp = this.handleMouseUp.bind(this);
        this._boundHandleTouchMove = this.handleMouseMove.bind(this);
        this._boundHandleTouchEnd = this.handleMouseUp.bind(this);

        this.applyStyles();
    }


    async initialize() {
        this.container = document.createElement('div');
        this.container.id = this.instanceId + '-wrapper';
        await this.render();

        await super.initialize();
    }

    async render() {
        this.createUI();
        this.setupEventListeners();
        return this.container;
    }


    connect(credentials, eventHandlers = {}) {
        if (this.isDestroyed || this.isEnabled) return;
        this.log.info("Softphone received connect command.");

        for (const [event, handler] of Object.entries(eventHandlers)) {
            this.on(event, handler);
        }

        this.config.server = credentials.server;
        this.config.username = credentials.username;
        this.config.password = credentials.password;
        this.config.callerIdName = credentials.callerIdName;

        this.enablePhone();
    }

    disconnect() {
        if (this.isDestroyed || !this.isEnabled) return;
        this.log.info("Softphone received disconnect command.");
        this.disablePhone();
    }


    show() {
        this.isHidden = false;
        this._updateUIState();
        this.emit('shown');
    }

    hide() {
        this.isHidden = true;
        this._updateUIState();
        this.emit('hidden');
    }

    toggleMinimize() {
        if (this.isHidden) {
            this.show();
        } else {
            this.hide();
        }
    }

    createUI() {
        const mainContainerClass = this.isHidden ? 'hidden' : '';

        const minimizeButtonHtml = this.config.hideMinimizeButton ? '' : `
            <button type="button" class="btn btn-sm btn-outline-secondary btn-minimize" title="Minimize">
                <i class="bi bi-dash-lg"></i>
            </button>`;

        const closeButtonHtml = this.config.hideCloseButton ? '' : `
            <button type="button" class="btn btn-sm btn-outline-danger btn-close-phone" title="Close">
                <i class="bi bi-x-lg"></i>
            </button>`;

        const hideButtonHtml = `
            <button type="button" class="btn btn-sm btn-outline-secondary btn-hide" title="Hide">
                <i class="bi bi-dash-lg"></i>
            </button>`;

        const extensionDisplay = this.config.callerIdName || this.config.username || '';

        this.container.innerHTML = `
            <div class="softphone-container ${mainContainerClass}" id="${this.instanceId}">
                <!-- The minimized icon is now completely removed -->
                <div class="softphone-main">
                    <div class="softphone-header">
                        <div class="d-flex align-items-center">
                            <div class="registration-status d-flex align-items-center me-3">
                                <span class="status-indicator ${this.isRegistered ? 'online' : 'offline'}"></span>
                                <span class="status-text small">${this.isRegistered ? 'Online' : 'Offline'}</span>
                            </div>
                            <div class="user-info small text-muted">${extensionDisplay}</div>
                        </div>
                        <div class="header-controls d-flex gap-1">
                            <button type="button" class="btn btn-sm btn-outline-secondary btn-toggle" title="Enable/Disable Phone">
                                <i class="bi bi-power"></i>
                            </button>
                            ${hideButtonHtml}
                            ${minimizeButtonHtml}
                            ${closeButtonHtml}
                        </div>
                    </div>
                    
                    <div class="softphone-body">
                        <div class="disabled-overlay ${this.isEnabled ? 'd-none' : ''}">
                            <div class="text-center p-4">
                                <i class="bi bi-telephone-x-fill  text-muted mb-3"></i>
                                <h6 class="text-muted">Phone Disabled</h6>
                                <p class="small text-muted mb-0">Click the power button to enable</p>
                            </div>
                        </div>
                        <div class="softphone-content ${this.isEnabled ? '' : 'd-none'}">
                            <div class="dialer-section">
                                <div class="input-group mb-3">
                                    <input type="tel" class="form-control phone-input" placeholder="Enter number" maxlength="20">
                                    <button class="btn btn-success btn-call" type="button">
                                        <i class="bi bi-telephone-fill me-1"></i>
                                        <span class="d-none d-md-inline">Call</span>
                                    </button>
                                </div>
                            </div>
                            <div class="incoming-call-section d-none">
                                <div class="card border-primary">
                                    <div class="card-body text-center">
                                        <i class="bi bi-telephone-inbound-fill display-4 text-primary mb-3"></i>
                                        <h6 class="card-title">Incoming Call</h6>
                                        <p class="card-text incoming-caller-id"></p>
                                        <div class="d-flex justify-content-center gap-2">
                                            <button type="button" class="btn btn-success btn-answer">
                                                <i class="bi bi-telephone-fill me-1"></i>Answer
                                            </button>
                                            <button type="button" class="btn btn-danger btn-decline">
                                                <i class="bi bi-telephone-x-fill me-1"></i>Decline
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="calls-section">
                                <div class="calls-list"></div>
                            </div>
                            <div class="call-controls d-none">
                                <div class="row g-2 mb-3">
                                    <div class="col-6 col-md-4"><button type="button" class="btn btn-outline-secondary btn-control btn-hold w-100"><i class="bi bi-pause-fill"></i><span class="d-none d-lg-inline ms-1">Hold</span></button></div>
                                    <div class="col-6 col-md-4"><button type="button" class="btn btn-outline-secondary btn-control btn-mute w-100"><i class="bi bi-mic-fill"></i><span class="d-none d-lg-inline ms-1">Mute</span></button></div>
                                    <div class="col-6 col-md-4"><button type="button" class="btn btn-outline-secondary btn-control btn-keypad w-100"><i class="bi bi-grid-3x3-gap-fill"></i><span class="d-none d-lg-inline ms-1">Keypad</span></button></div>
                                    <div class="col-6 col-md-5"><button type="button" class="btn btn-outline-secondary btn-control btn-transfer w-100"><i class="bi bi-arrow-right-square"></i><span class="d-none d-lg-inline ms-1">Transfer</span></button></div>
                                    <div class="col-12 col-md-7"><button type="button" class="btn btn-danger btn-control btn-hangup w-100"><i class="bi bi-telephone-x-fill me-1"></i>Hang Up</button></div>
                                </div>
                                <div class="dtmf-pad d-none">
                                    <div class="card">
                                        <div class="card-header d-flex justify-content-between align-items-center py-2"><span class="small fw-bold">Keypad</span><button type="button" class="btn-close btn-close-keypad" aria-label="Close"></button></div>
                                        <div class="card-body p-2"><div class="row g-1">${this.generateDTMFButtons()}</div></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    setupEventListeners() {
        const container = this.container.querySelector('.softphone-container');
        if (!container) return;

        this._boundHandleMouseMove = this.handleMouseMove.bind(this);
        this._boundHandleMouseUp = this.handleMouseUp.bind(this);
        this._boundHandleTouchMove = this.handleMouseMove.bind(this);
        this._boundHandleTouchEnd = this.handleMouseUp.bind(this);

        this.setupDragging(container);

        const btnToggle = container.querySelector('.btn-toggle');
        const btnMinimize = container.querySelector('.btn-minimize');
        const btnClose = container.querySelector('.btn-close-phone');
        const btnHide = container.querySelector('.btn-hide');

        if (btnToggle) btnToggle.addEventListener('click', (e) => { e.stopPropagation(); this.togglePhone(); });
        if (btnMinimize) btnMinimize.addEventListener('click', (e) => { e.stopPropagation(); this.minimizePhone(); });
        if (btnClose) btnClose.addEventListener('click', (e) => { e.stopPropagation(); this.destroy(); });
        if (btnHide) btnHide.addEventListener('click', (e) => { e.stopPropagation(); this.hide(); });

        // Use event delegation with more specific handling
        container.addEventListener('click', (event) => {
            event.stopPropagation();
            const target = event.target.closest('button');
            if (!target) return;

            this.log.info(`Button clicked: ${target.className}`);

            if (target.classList.contains('btn-call')) {
                const number = container.querySelector('.phone-input').value.trim();
                if (number) this.makeCall(number);
            } else if (target.classList.contains('btn-answer')) {
                this.log.info("Answer button clicked");
                // Get the call ID from the incoming call section
                const incomingSection = container.querySelector('.incoming-call-section');
                const callId = incomingSection ? incomingSection.getAttribute('data-call-id') : null;
                this.log.info(`Attempting to answer call: ${callId}`);
                if (callId && this.activeCalls.has(callId)) {
                    this.answerCall(callId);
                } else {
                    this.log.warn(`No valid incoming call found. CallId: ${callId}, CallExists: ${callId ? this.activeCalls.has(callId) : false}`);
                }
            } else if (target.classList.contains('btn-decline')) {
                this.log.info("Decline button clicked");
                // Get the call ID from the incoming call section
                const incomingSection = container.querySelector('.incoming-call-section');
                const callId = incomingSection ? incomingSection.getAttribute('data-call-id') : null;
                this.log.info(`Attempting to decline call: ${callId}`);
                if (callId && this.activeCalls.has(callId)) {
                    this.hangupCall(callId);
                } else {
                    this.log.warn(`No valid incoming call found to decline. CallId: ${callId}`);
                }
            } else if (target.classList.contains('btn-keypad')) {
                this.toggleDialpad();
            } else if (target.classList.contains('btn-close-keypad')) {
                this.hideDialpad();
            } else if (target.classList.contains('dtmf-btn')) {
                this.sendDTMF(target.dataset.tone);
            } else if (target.classList.contains('btn-hold')) {
                this.toggleHold();
            } else if (target.classList.contains('btn-mute')) {
                this.toggleMute();
            } else if (target.classList.contains('btn-transfer')) {
                this.transferCall();
            } else if (target.classList.contains('btn-hangup')) {
                this.hangupCurrentCall();
            }
        });

        const phoneInput = container.querySelector('.phone-input');
        if (phoneInput) {
            phoneInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                    const number = phoneInput.value.trim();
                    if (number) this.makeCall(number);
                }
            });
        }
    }


    setupDragging(container) {
        const header = container.querySelector('.softphone-header');
        if (!header) return;

        let isDragging = false;
        let startX, startY, initialX, initialY;

        const handleMouseDown = (e) => {
            if (e.target.closest('button')) return;

            isDragging = true;
            container.classList.add('dragging');

            // --- START DRAGGING FIX ---
            // Store the initial position from the element's style, not getBoundingClientRect
            const style = window.getComputedStyle(container);
            initialX = parseInt(style.left, 10) || (window.innerWidth - container.offsetWidth - 20); // Default to initial right: 20px
            initialY = parseInt(style.top, 10) || 20; // Default to initial top: 20px
            // --- END DRAGGING FIX ---

            startX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
            startY = e.type === 'mousedown' ? e.clientY : e.touches[0].clientY;

            // Store these on the instance for access in the bound handlers
            this.isDragging = isDragging;
            this.startX = startX;
            this.startY = startY;
            this.initialX = initialX;
            this.initialY = initialY;

            e.preventDefault();
        };

        header.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('mousemove', this._boundHandleMouseMove);
        document.addEventListener('mouseup', this._boundHandleMouseUp);
        header.addEventListener('touchstart', handleMouseDown, { passive: false });
        document.addEventListener('touchmove', this._boundHandleTouchMove, { passive: false });
        document.addEventListener('touchend', this._boundHandleTouchEnd);
    }

    handleMouseMove(e) {
        const container = this.container.querySelector('.softphone-container');
        if (!this.isDragging || !container) return;

        e.preventDefault();

        const currentX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
        const currentY = e.type === 'mousemove' ? e.clientY : e.touches[0].clientY;

        const deltaX = currentX - this.startX;
        const deltaY = currentY - this.startY;

        // Apply a transform for smooth, performant dragging.
        // We are not changing the final top/left properties here.
        container.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
    }

    handleMouseUp(e) {
        const container = this.container.querySelector('.softphone-container');
        if (this.isDragging) {
            this.isDragging = false;
            if (container) {
                container.classList.remove('dragging');
                const currentX = e.type === 'mouseup' ? e.clientX : e.changedTouches[0].clientX;
                const currentY = e.type === 'mouseup' ? e.clientY : e.changedTouches[0].clientY;

                const deltaX = currentX - this.startX;
                const deltaY = currentY - this.startY;

                const newX = this.initialX + deltaX;
                const newY = this.initialY + deltaY;

                // Constrain to viewport
                const maxX = window.innerWidth - container.offsetWidth;
                const maxY = window.innerHeight - container.offsetHeight;
                const constrainedX = Math.max(0, Math.min(newX, maxX));
                const constrainedY = Math.max(0, Math.min(newY, maxY));

                // Apply the final position and reset the transform.
                container.style.transform = '';
                container.style.left = `${constrainedX}px`;
                container.style.top = `${constrainedY}px`;
                container.style.right = 'auto'; // Important to override the initial 'right' positioning
            }
        }
    }


    generateDTMFButtons() {
        const buttons = [
            ['1', '2', '3'],
            ['4', '5', '6'],
            ['7', '8', '9'],
            ['*', '0', '#']
        ];

        return buttons.map(row =>
            `<div class="col-12">
                <div class="row g-1">
                    ${row.map(btn =>
                `<div class="col-4">
                            <button type="button" class="btn btn-outline-secondary dtmf-btn w-100" data-tone="${btn}">${btn}</button>
                        </div>`
            ).join('')}
                </div>
            </div>`
        ).join('');
    }

    togglePhone() {
        if (this.isEnabled) {
            this.disablePhone();
        } else {
            this.enablePhone();
        }
        this._updateUIState();
        this.emit('phone_toggled', { enabled: this.isEnabled });
    }


    enablePhone() {
        this.isEnabled = true;
        this.setupSIPJS();
        this.register();
        this.startReconnectMonitoring();
    }

    disablePhone() {
        this.isEnabled = false;
        this.stopReconnectMonitoring();

        // Hang up all active calls
        const hangupPromises = Array.from(this.activeCalls.keys()).map(id => this.hangupCall(id));
        Promise.all(hangupPromises).then(() => {
            if (this.registerer) {
                this.registerer.unregister().catch(console.warn);
            }
            if (this.userAgent) {
                this.userAgent.stop().catch(console.warn);
            }
            this.isRegistered = false;
        });
    }

    minimizePhone() {
        this.isMinimized = true;
        this._updateUIState();
        this.emit('phone_minimized');
    }

    restorePhone() {
        this.isMinimized = false;
        this._updateUIState();
        this.emit('phone_restored');
    }


    startReconnectMonitoring() {
        if (!this.isEnabled) return;

        this.stopReconnectMonitoring();
        this.reconnectInterval = setInterval(() => {
            if (this.isEnabled && !this.isRegistered && !this.isDestroyed) {
                this.attemptReconnect();
            }
        }, 5000); // Check every 5 seconds
    }

    stopReconnectMonitoring() {
        if (this.reconnectInterval) {
            clearInterval(this.reconnectInterval);
            this.reconnectInterval = null;
        }
    }

    attemptReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.warn('Max reconnect attempts reached');
            return;
        }

        this.reconnectAttempts++;
        console.log(`Reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);

        this.updateRegistrationStatus('registering', `Reconnecting... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

        // Clean up existing connections
        if (this.userAgent) {
            this.userAgent.stop().catch(console.warn);
        }

        // Reinitialize
        setTimeout(() => {
            if (this.isEnabled && !this.isDestroyed) {
                this.setupSIPJS();
                this.register();
            }
        }, 1000);
    }

    setupSIPJS() {
        if (!this.isEnabled) return;

        try {
            const wsUri = this.config.server;
            const userAgentOptions = {
                uri: SIP.UserAgent.makeURI(`sip:${this.config.username}@${this.extractDomain(wsUri)}`),
                transportOptions: { server: wsUri, traceSip: false },
                authorizationUsername: this.config.username,
                authorizationPassword: this.config.password,
                displayName: this.config.callerIdName || this.config.username,
                traceSip: false,
                logLevel: "error", // or "off" if supported
                logBuiltinEnabled: false,

                sessionDescriptionHandlerFactoryOptions: {
                    peerConnectionConfiguration: {
                        iceServers: [
                            { urls: "stun:stun.l.google.com:19302" },
                            { urls: "stun:stun1.l.google.com:19302" },
                            { urls: "stun:stun2.l.google.com:19302" },
                            { urls: "stun:stun3.l.google.com:19302" },
                            // If you have a TURN server, add it here:
                            // { urls: "turn:turn.example.com:3478", username: "turnuser", credential: "turnpass" }
                        ],
                        iceTransportPolicy: "all",
                        iceCandidatePoolSize: 10,
                        bundlePolicy: "balanced",
                        rtcpMuxPolicy: "require",
                    },
                    alwaysAcquireMediaFirst: true,
                    constraints: { audio: true, video: false }
                },

            };

            this.userAgent = new SIP.UserAgent(userAgentOptions);
            this.userAgent.delegate = {
                onConnect: () => {
                    this.reconnectAttempts = 0; // Reset on successful connection
                    if (this.registerer) this.registerer.register();
                },
                onDisconnect: () => {
                    this.isRegistered = false;
                    this.updateRegistrationStatus('offline', 'Disconnected');
                    this.emit('unregistered');

                    if (this.isEnabled) {
                        this.startReconnectMonitoring();
                    }
                },
                onInvite: (invitation) => { this.handleIncomingCall(invitation); }
            };

            this.userAgent.start().catch(e => {
                console.error('Failed to start user agent:', e);
                this.updateRegistrationStatus('offline', 'Failed to start');
            });
        } catch (error) {
            console.error('Error setting up SIP.js:', error);
            this.updateRegistrationStatus('offline', 'Setup failed');
        }
    }

    register() {
        if (!this.userAgent || !this.isEnabled) return;

        this.registerer = new SIP.Registerer(this.userAgent);
        this.registerer.stateChange.addListener((newState) => {
            switch (newState) {
                case SIP.RegistererState.Registered:
                    this.isRegistered = true;
                    this.reconnectAttempts = 0;
                    this.updateRegistrationStatus('online', 'Registered');
                    this.emit('registered');
                    break;
                case SIP.RegistererState.Unregistered:
                    this.isRegistered = false;
                    this.updateRegistrationStatus('offline', 'Unregistered');
                    this.emit('unregistered');
                    break;
                default:
                    this.isRegistered = false;
                    this.updateRegistrationStatus('registering', 'Registering...');
                    this.emit('registering');
            }
            this._updateUIState();
        });

        this.registerer.register().catch(e => {
            console.error('Registration failed:', e);
            if (this.isEnabled) {
                this.startReconnectMonitoring();
            }
        });
    }

    makeCall(target) {
        if (!this.isRegistered || this.isDialing || !this.isEnabled) {
            this.log.warn("Cannot make call. Not registered, already dialing, or phone is disabled.");
            return null;
        }
        this.isDialing = true;
        this._updateUIState(); // Update UI to show dialing state immediately

        try {
            const domain = this.extractDomain(this.config.server);
            const targetURI = SIP.UserAgent.makeURI(`sip:${target}@${domain}`);
            if (!targetURI) {
                throw new Error(`Invalid target URI for ${target}`);
            }

            // Options for the Inviter constructor.
            // *** FIX: Initialize the delegate property to an empty object. ***
            const inviterOptions = {
                earlyMedia: true,
                sessionDescriptionHandlerOptions: {
                    constraints: { audio: true, video: false }
                },
                delegate: {} // This prevents the "cannot set properties of undefined" error.
            };

            const inviter = new SIP.Inviter(this.userAgent, targetURI, inviterOptions);
            const sessionId = inviter.id;

            const callObj = {
                id: sessionId,
                session: inviter,
                number: target,
                direction: 'outbound',
                status: 'calling',
                isHeld: false,
                isMuted: false,
                connectTime: null
            };

            this.activeCalls.set(sessionId, callObj);
            this.currentCallId = sessionId;

            // Attach all necessary event handlers *before* sending the INVITE.
            this.setupCallEventHandlers(inviter, sessionId);

            // Send the INVITE request.
            inviter.invite().then(() => {
                this.log.info(`INVITE sent for call ${sessionId}`);
            }).catch(err => {
                this.log.error(`INVITE failed for call ${sessionId}:`, err);
                this.activeCalls.delete(sessionId);
            }).finally(() => {
                this.isDialing = false;
                this._updateUIState();
            });

            this.emit('call_started', { callId: sessionId, direction: 'outbound', number: target });
            this._updateUIState();
            return sessionId;

        } catch (error) {
            this.log.error('Error making call:', error);
            this.isDialing = false;
            this._updateUIState();
            return null;
        }
    }

    setupEarlyMediaAudio(sessionDescriptionHandler, callId) {
        if (!sessionDescriptionHandler || !sessionDescriptionHandler.peerConnection) return;

        const pc = sessionDescriptionHandler.peerConnection;

        // Listen for remote audio tracks immediately
        pc.getReceivers().forEach(receiver => {
            if (receiver.track && receiver.track.kind === 'audio') {
                const remoteStream = new MediaStream([receiver.track]);
                this.playRemoteAudio(remoteStream, callId);
            }
        });
    }

    handleIncomingCall(invitation) {
        const sessionId = invitation.id;
        if (this.activeCalls.has(sessionId)) return;

        this.log.info(`Incoming call received: ${sessionId}`);

        const remoteIdentity = invitation.remoteIdentity;
        const number = remoteIdentity.displayName || remoteIdentity.uri.user || 'Unknown';

        const callObj = {
            id: sessionId,
            session: invitation,
            number: number,
            direction: 'inbound',
            status: 'ringing',
            isHeld: false,
            isMuted: false
        };

        this.activeCalls.set(sessionId, callObj);
        this.setupCallEventHandlers(invitation, sessionId);

        // Show the softphone if it's hidden when an incoming call arrives
        if (this.isHidden) {
            this.log.info(`Auto-showing softphone for incoming call`);
            this.show();
        }

        // Use setTimeout to ensure UI is ready
        setTimeout(() => {
            this.showIncomingCallUI(sessionId, number);
            this._updateUIState();
        }, 100);

        this.emit('call_received', { callId: sessionId, direction: 'inbound', number: number });
    }

    answerCall(callId) {
        const callObj = this.activeCalls.get(callId);
        if (!callObj || callObj.status !== 'ringing' || callObj.direction !== 'inbound') {
            this.log.warn(`Cannot answer call ${callId}: invalid call state`);
            return;
        }

        const options = {
            sessionDescriptionHandlerOptions: {
                constraints: { audio: true, video: false }
            }
        };

        callObj.session.accept(options).then(() => {
            this.log.info(`Call ${callId} accepted`);
            // Clear the data-call-id from incoming section since call is no longer ringing
            const incomingSection = this.container.querySelector('.incoming-call-section');
            if (incomingSection) {
                incomingSection.removeAttribute('data-call-id');
            }
        }).catch(e => {
            this.log.error('Failed to answer call:', e);
            this.emit('call_answer_failed', { callId, error: e });
        });
    }


    setupCallEventHandlers(session, callId) {
        const callObj = this.activeCalls.get(callId);
        if (!callObj) return;

        this.log.info(`Setting up event handlers for call ${callId}`);

        // Initialize delegate object if it doesn't exist
        if (!session.delegate) {
            session.delegate = {};
        }

        // Set up session description handler for media - this approach works for 0.21.2
        session.delegate.onSessionDescriptionHandler = (sdh) => {
            this.log.info(`SessionDescriptionHandler created for call ${callId}`);

            if (!sdh.peerConnection) {
                this.log.error(`PeerConnection not found for call ${callId}`);
                return;
            }

            const pc = sdh.peerConnection;
            this.log.info(`Setting up PeerConnection event handlers for call ${callId}`);

            pc.addEventListener('track', (event) => {
                this.log.info(`Remote track received for call ${callId}. Kind: ${event.track.kind}`);
                const stream = event.streams && event.streams[0] ? event.streams[0] : new MediaStream([event.track]);
                this.playRemoteAudio(stream, callId);
            });

            pc.addEventListener('iceconnectionstatechange', () => {
                this.log.debug(`ICE connection state for ${callId}: ${pc.iceConnectionState}`);
                if (pc.iceConnectionState === 'failed') {
                    this.emit('ice_failed', { callId });
                }
            });

            // For incoming calls, we might already have remote tracks
            pc.getReceivers().forEach(receiver => {
                if (receiver.track && receiver.track.kind === 'audio') {
                    this.log.info(`Found existing audio receiver for call ${callId}`);
                    const stream = new MediaStream([receiver.track]);
                    this.playRemoteAudio(stream, callId);
                }
            });
        };

        // Handle session state changes
        session.stateChange.addListener((newState) => {
            const currentCall = this.activeCalls.get(callId);
            if (!currentCall) return;

            this.log.info(`Call ${callId} state changed to ${newState}`);

            switch (newState) {
                case SIP.SessionState.Establishing:
                    currentCall.status = 'connecting';
                    break;

                case SIP.SessionState.Established:
                    currentCall.status = 'connected';
                    currentCall.connectTime = new Date();
                    this.currentCallId = callId;

                    // Clear incoming call UI data if this was an answered incoming call
                    if (currentCall.direction === 'inbound') {
                        const incomingSection = this.container.querySelector('.incoming-call-section');
                        if (incomingSection && incomingSection.getAttribute('data-call-id') === callId) {
                            incomingSection.removeAttribute('data-call-id');
                        }
                    }

                    this.emit('call_answered', { callId });
                    break;

                case SIP.SessionState.Terminated:
                    this.stopLocalMedia(callId);

                    // Clean up incoming call UI if this was a terminated ringing call
                    if (currentCall.status === 'ringing') {
                        const incomingSection = this.container.querySelector('.incoming-call-section');
                        if (incomingSection && incomingSection.getAttribute('data-call-id') === callId) {
                            incomingSection.removeAttribute('data-call-id');
                        }
                    }

                    this.activeCalls.delete(callId);

                    if (this.currentCallId === callId) {
                        this.currentCallId = this.getNextActiveCallId();
                    }

                    this.emit('call_terminated', { callId });
                    break;
            }
            this._updateUIState();
        });
    }


    toggleHold() {
        const callObj = this.getCurrentCall();
        if (!callObj) return;
        if (callObj.isHeld) this.unholdCall(callObj.id);
        else this.holdCall(callObj.id);
    }

    async holdCall(callId) {
        const callObj = this.activeCalls.get(callId);
        if (!callObj || callObj.status !== 'connected') return;
        try {
            const options = { sessionDescriptionHandlerModifiers: [SIP.Web.holdModifier] };
            await callObj.session.invite(options);
            callObj.isHeld = true;
            callObj.status = 'held';
            this.emit('call_held', { callId });
            this._updateUIState();
        } catch (error) {
            console.error(`Failed to hold call ${callId}:`, error);
        }
    }

    async unholdCall(callId) {
        const callObj = this.activeCalls.get(callId);
        if (!callObj || !callObj.isHeld) return;
        try {
            const options = { sessionDescriptionHandlerModifiers: (SIP.Web.unholdModifier ? [SIP.Web.unholdModifier] : []) };
            await callObj.session.invite(options);
            callObj.isHeld = false;
            callObj.status = 'connected';
            this.emit('call_unheld', { callId });
            this._updateUIState();
        } catch (error) {
            console.error(`Failed to unhold call ${callId}:`, error);
        }
    }

    toggleMute() {
        const callObj = this.getCurrentCall();
        if (!callObj) return;
        if (callObj.isMuted) this.unmuteCall(callObj.id);
        else this.muteCall(callObj.id);
    }

    muteCall(callId) {
        const callObj = this.activeCalls.get(callId);
        if (!callObj) return;
        const handler = callObj.session.sessionDescriptionHandler;
        if (handler && handler.localMediaStream) {
            handler.localMediaStream.getAudioTracks().forEach(track => { track.enabled = false; });
            callObj.isMuted = true;
            this.emit('muted', { callId });
            this._updateUIState();
        }
    }

    unmuteCall(callId) {
        const callObj = this.activeCalls.get(callId);
        if (!callObj) return;
        const handler = callObj.session.sessionDescriptionHandler;
        if (handler && handler.localMediaStream) {
            handler.localMediaStream.getAudioTracks().forEach(track => { track.enabled = true; });
            callObj.isMuted = false;
            this.emit('unmuted', { callId });
            this._updateUIState();
        }
    }

    sendDTMF(tone) {
        const callObj = this.getCurrentCall();
        if (!callObj || callObj.status !== 'connected') return;
        callObj.session.info('application/dtmf-relay', `Signal=${tone}\r\nDuration=250`);
        this.emit('dtmf_sent', { callId: callObj.id, tone });
    }

    transferCall() {
        const target = prompt('Enter transfer destination:');
        if (!target || !this.getCurrentCall()) return;
        const targetURI = SIP.UserAgent.makeURI(`sip:${target}@${this.extractDomain(this.config.server)}`);
        this.getCurrentCall().session.refer(targetURI);
    }

    hangupCurrentCall() {
        if (this.currentCallId) this.hangupCall(this.currentCallId);
    }

    hangupCall(callId) {
        const callObj = this.activeCalls.get(callId);
        if (!callObj) return;
        this.stopLocalMedia(callId);
        this.terminateSessionSafely(callObj.session, callObj);
    }

    async terminateSessionSafely(session, callObj) {
        if (!session) return;

        try {
            this.log.info(`Terminating call ${callObj.id} in state: ${session.state}, direction: ${callObj.direction}, status: ${callObj.status}`);

            // Special handling for incoming calls that are still ringing
            if (callObj.direction === 'inbound' && callObj.status === 'ringing') {
                if (session instanceof SIP.Invitation && typeof session.reject === 'function') {
                    await session.reject({ statusCode: 603 });
                    this.log.info(`Incoming call ${callObj.id} rejected`);
                    return;
                }
            }

            switch (session.state) {
                case SIP.SessionState.Initial:
                case SIP.SessionState.Establishing:
                    // For outgoing calls that are still establishing
                    if (session instanceof SIP.Inviter) {
                        await session.cancel();
                        this.log.info(`Outgoing call ${callObj.id} cancelled`);
                    }
                    // For incoming calls that haven't been answered yet
                    else if (session instanceof SIP.Invitation) {
                        await session.reject({ statusCode: 603 });
                        this.log.info(`Incoming call ${callObj.id} rejected`);
                    }
                    break;

                case SIP.SessionState.Established:
                    // Active calls need a BYE
                    await session.bye();
                    this.log.info(`Call ${callObj.id} terminated with BYE`);
                    break;

                case SIP.SessionState.Terminating:
                case SIP.SessionState.Terminated:
                    // Already ending/ended
                    this.log.info(`Call ${callObj.id} already terminated/terminating`);
                    break;

                default:
                    this.log.warn(`Unexpected session state ${session.state} for call ${callObj.id}`);
                    // Try the appropriate method based on session type and call state
                    if (callObj.direction === 'inbound' && callObj.status === 'ringing' && session instanceof SIP.Invitation) {
                        await session.reject({ statusCode: 603 });
                        this.log.info(`Incoming ringing call ${callObj.id} rejected`);
                    } else if (session instanceof SIP.Inviter && typeof session.cancel === 'function') {
                        await session.cancel();
                    } else if (typeof session.bye === 'function') {
                        await session.bye();
                    }
                    break;
            }
        } catch (err) {
            this.log.warn(`Error during session termination for call ${callObj.id}:`, err.message);
            // Don't throw - just log and continue cleanup
        }

        // Clean up the incoming call UI reference if this was a ringing call
        if (callObj.status === 'ringing') {
            const incomingSection = this.container.querySelector('.incoming-call-section');
            if (incomingSection && incomingSection.getAttribute('data-call-id') === callObj.id) {
                incomingSection.removeAttribute('data-call-id');
            }
        }
    }


    _updateUIState() {
        const container = this.container.querySelector('.softphone-container');
        if (!container) return;

        // Update minimized state classes
        container.classList.toggle('hidden', this.isHidden);

        // Update main sections visibility
        const softphoneIcon = container.querySelector('.softphone-icon');
        const softphoneMain = container.querySelector('.softphone-main');
        const disabledOverlay = container.querySelector('.disabled-overlay');
        const softphoneContent = container.querySelector('.softphone-content');

        if (softphoneIcon && softphoneMain) {
            if (this.isMinimized) {
                softphoneIcon.classList.remove('d-none');
                softphoneMain.classList.add('d-none');
            } else {
                softphoneIcon.classList.add('d-none');
                softphoneMain.classList.remove('d-none');
            }
        }

        // Update enabled/disabled state
        if (disabledOverlay && softphoneContent) {
            if (this.isEnabled) {
                disabledOverlay.classList.add('d-none');
                softphoneContent.classList.remove('d-none');
            } else {
                disabledOverlay.classList.remove('d-none');
                softphoneContent.classList.add('d-none');
            }
        }

        // Update icon badges
        if (softphoneIcon) {
            const statusBadge = softphoneIcon.querySelector('.status-badge');
            if (statusBadge) {
                statusBadge.classList.toggle('d-none', !this.isRegistered);
            }

            // Update call count badge
            let callBadge = softphoneIcon.querySelector('.call-badge');
            const activeCallsCount = this.getActiveCallsCount();

            if (activeCallsCount > 0) {
                if (!callBadge) {
                    callBadge = document.createElement('span');
                    callBadge.className = 'position-absolute top-0 start-0 translate-middle badge rounded-pill bg-danger call-badge';
                    callBadge.style.fontSize = '0.6rem';
                    callBadge.style.minWidth = '18px';
                    callBadge.style.height = '18px';
                    callBadge.style.lineHeight = '1';
                    softphoneIcon.appendChild(callBadge);
                }
                callBadge.textContent = activeCallsCount;
                callBadge.classList.remove('d-none');
            } else if (callBadge) {
                callBadge.classList.add('d-none');
            }
        }

        if (this.isMinimized) return; // Don't update detailed UI when minimized

        const dialerSection = container.querySelector('.dialer-section');
        const incomingSection = container.querySelector('.incoming-call-section');
        const callsList = container.querySelector('.calls-list');
        const callControls = container.querySelector('.call-controls');

        if (!dialerSection || !incomingSection || !callsList || !callControls) return;

        const currentCall = this.getCurrentCall();
        const ringingCall = Array.from(this.activeCalls.values()).find(c => c.status === 'ringing');

        // 1. Determine which main section is visible
        if (ringingCall) {
            dialerSection.classList.add('d-none');
            incomingSection.classList.remove('d-none');
            callControls.classList.add('d-none');
        } else if (this.activeCalls.size > 0) {
            dialerSection.classList.add('d-none');
            incomingSection.classList.add('d-none');
            callControls.classList.remove('d-none');
        } else { // Idle state
            dialerSection.classList.remove('d-none');
            incomingSection.classList.add('d-none');
            callControls.classList.add('d-none');
        }

        // 2. Update the calls list
        callsList.innerHTML = '';
        this.activeCalls.forEach((callObj, callId) => {
            if (callObj.status === 'ringing') return;

            const callElement = document.createElement('div');
            callElement.className = `call-item ${callId === this.currentCallId ? 'active' : ''}`;
            callElement.addEventListener('click', () => this.switchToCall(callId));

            const statusIcon = this.getCallStatusIcon(callObj);
            callElement.innerHTML = `
                <div class="call-info">
                    <div>
                        <div class="fw-bold">${callObj.number}</div>
                        <div class="small text-muted">${statusIcon} ${this.getCallStatusText(callObj)}</div>
                    </div>
                    <div class="call-duration text-end">
                        <div class="fw-bold">${this.getCallDuration(callObj)}</div>
                        <div class="small text-muted">${callObj.direction === 'inbound' ? 'Incoming' : 'Outgoing'}</div>
                    </div>
                </div>
            `;
            callsList.appendChild(callElement);
        });

        // 3. Update button states
        const btnCall = container.querySelector('.btn-call');
        const btnHold = container.querySelector('.btn-hold');
        const btnMute = container.querySelector('.btn-mute');
        const btnTransfer = container.querySelector('.btn-transfer');
        const btnHangup = container.querySelector('.btn-hangup');
        const btnToggle = container.querySelector('.btn-toggle');

        if (btnCall) {
            btnCall.disabled = this.isDialing || !this.isRegistered || !this.isEnabled;
            const callIcon = btnCall.querySelector('i');
            if (this.isDialing && callIcon) {
                callIcon.className = 'bi bi-hourglass-split me-1';
            } else if (callIcon) {
                callIcon.className = 'bi bi-telephone-fill me-1';
            }
        }

        if (btnToggle) {
            const toggleIcon = btnToggle.querySelector('i');
            if (toggleIcon) {
                toggleIcon.className = this.isEnabled ? 'bi bi-power' : 'bi bi-power text-danger';
            }
            btnToggle.classList.toggle('btn-outline-success', this.isEnabled);
            btnToggle.classList.toggle('btn-outline-secondary', !this.isEnabled);
        }

        if (btnHangup) btnHangup.disabled = !currentCall;

        const isCallEstablished = currentCall && (currentCall.status === 'connected' || currentCall.status === 'held');
        const isCallConnected = currentCall && currentCall.status === 'connected';

        if (btnHold) {
            btnHold.disabled = !isCallEstablished;
            const holdIcon = btnHold.querySelector('i');
            const holdText = btnHold.querySelector('span');

            if (currentCall && currentCall.isHeld) {
                if (holdIcon) holdIcon.className = 'bi bi-play-fill';
                if (holdText) holdText.textContent = 'Resume';
                btnHold.classList.add('active');
            } else {
                if (holdIcon) holdIcon.className = 'bi bi-pause-fill';
                if (holdText) holdText.textContent = 'Hold';
                btnHold.classList.remove('active');
            }
        }

        if (btnMute) {
            btnMute.disabled = !isCallConnected;
            const muteIcon = btnMute.querySelector('i');
            const muteText = btnMute.querySelector('span');

            if (currentCall && currentCall.isMuted) {
                if (muteIcon) muteIcon.className = 'bi bi-mic-mute-fill';
                if (muteText) muteText.textContent = 'Unmute';
                btnMute.classList.add('active');
            } else {
                if (muteIcon) muteIcon.className = 'bi bi-mic-fill';
                if (muteText) muteText.textContent = 'Mute';
                btnMute.classList.remove('active');
            }
        }

        if (btnTransfer) {
            btnTransfer.disabled = !isCallConnected;
        }

        // 4. Manage the timer interval
        const hasConnectedCalls = Array.from(this.activeCalls.values()).some(c => c.connectTime);
        if (hasConnectedCalls && !this.timerInterval) {
            this.timerInterval = setInterval(() => this._updateUIState(), 1000);
        } else if (!hasConnectedCalls && this.timerInterval) {
            clearInterval(this.timerInterval);
            this.timerInterval = null;
        }
    }

    showIncomingCallUI(callId, number) {
        this.log.info(`Showing incoming call UI for ${callId} from ${number}`);

        const container = this.container.querySelector('.softphone-container');
        const incomingSection = container.querySelector('.incoming-call-section');
        const callerIdElement = container.querySelector('.incoming-caller-id');

        if (callerIdElement) {
            callerIdElement.textContent = number;
        }

        // Store the call ID on the incoming call section for reference
        if (incomingSection) {
            incomingSection.setAttribute('data-call-id', callId);
            this.log.info(`Set data-call-id to ${callId} on incoming section`);
        }

        // Force update UI state to show incoming call
        this._updateUIState();

        // Log current state for debugging
        this.log.info(`Active calls: ${this.activeCalls.size}`);
        this.log.info(`Current UI state - isHidden: ${this.isHidden}, isEnabled: ${this.isEnabled}`);
    }

    toggleDialpad() {
        const dialpad = this.container.querySelector('.dtmf-pad');
        if (dialpad) {
            dialpad.classList.toggle('d-none');
            const isVisible = !dialpad.classList.contains('d-none');
            this.emit('keypad_toggled', { visible: isVisible });
        }
    }

    hideDialpad() {
        const dialpad = this.container.querySelector('.dtmf-pad');
        if (dialpad) {
            dialpad.classList.add('d-none');
            this.emit('keypad_hidden');
        }
    }

    getCallStatusIcon(callObj) {
        const iconMap = {
            'calling': '📞',
            'ringing': '📞',
            'connecting': '🔄',
            'connected': '✅',
            'held': '⏸️'
        };
        return iconMap[callObj.status] || '📞';
    }

    getCallStatusText(callObj) {
        const statusMap = {
            'calling': 'Calling...',
            'ringing': 'Ringing...',
            'connecting': 'Connecting...',
            'connected': 'Connected',
            'held': 'On Hold'
        };
        return statusMap[callObj.status] || callObj.status;
    }

    getActiveCallsCount() {
        return Array.from(this.activeCalls.values()).filter(call =>
            call.status !== 'ringing'
        ).length;
    }

    getIncomingCallId() {
        // First try to get it from the UI
        const incomingSection = this.container.querySelector('.incoming-call-section');
        const uiCallId = incomingSection ? incomingSection.getAttribute('data-call-id') : null;

        if (uiCallId && this.activeCalls.has(uiCallId)) {
            const call = this.activeCalls.get(uiCallId);
            if (call.status === 'ringing') {
                return uiCallId;
            }
        }

        // Fallback to searching through all calls
        const ringingCall = Array.from(this.activeCalls.values()).find(c => c.status === 'ringing');
        return ringingCall ? ringingCall.id : null;
    }

    on(event, callback) {
        if (!this.eventListeners[event]) {
            this.eventListeners[event] = [];
        }
        this.eventListeners[event].push(callback);
    }

    off(event, callback) {
        if (!this.eventListeners[event]) return;
        const index = this.eventListeners[event].indexOf(callback);
        if (index > -1) {
            this.eventListeners[event].splice(index, 1);
        }
    }

    emit(event, data) {
        if (!this.eventListeners[event]) return;
        this.eventListeners[event].forEach(callback => {
            try {
                callback(data);
            } catch (error) {
                console.error(`Error in '${event}' event callback:`, error);
            }
        });
    }

    playRemoteAudio(stream, callId) {
        if (!stream) return;
        try {
            let audioElement = document.getElementById(`audio-${callId}`);
            if (!audioElement) {
                audioElement = document.createElement('audio');
                audioElement.id = `audio-${callId}`;
                audioElement.autoplay = true;
                audioElement.playsInline = true;
                audioElement.style.display = 'none';
                // Important: do not set muted here unless you will prompt user to unmute
                document.body.appendChild(audioElement);
            }

            // If stream is already the same object, don't reassign unnecessarily
            if (audioElement.srcObject !== stream) {
                audioElement.srcObject = stream;
            }

            // Try to start playback (may be blocked by autoplay policy)
            const playPromise = audioElement.play();
            if (playPromise && typeof playPromise.then === 'function') {
                playPromise.catch((err) => {
                    // Autoplay blocked — notify and set a flag so UI can request user gesture
                    console.warn('Audio play() was blocked by browser autoplay policy:', err);
                    // Emit an event so the application can show a "Click to unmute/enable audio" UI
                    this.emit('autoplay_blocked', { callId, error: err });
                    // Optionally we can leave the audio element in place and wait for user gesture to call .play() again.
                });
            }
        } catch (err) {
            console.error('playRemoteAudio error', err);
        }
    }


    stopLocalMedia(callId) {
        const audioElement = document.getElementById(`audio-${callId}`);
        if (audioElement) {
            audioElement.pause();
            audioElement.srcObject = null;
            audioElement.remove();
        }
    }

    updateRegistrationStatus(status, message) {
        const container = this.container.querySelector('.softphone-container');
        if (!container) return;

        const indicator = container.querySelector('.status-indicator');
        const text = container.querySelector('.status-text');

        if (indicator) {
            indicator.className = `status-indicator ${status}`;
        }
        if (text) {
            text.textContent = message;
        }
    }

    switchToCall(callId) {
        if (this.activeCalls.has(callId)) {
            this.currentCallId = callId;
            this.emit('call_switched', { callId });
            this._updateUIState();
        }
    }

    getCurrentCall() {
        return this.currentCallId ? this.activeCalls.get(this.currentCallId) : null;
    }

    getNextActiveCallId() {
        // Return the ID of the first non-ringing call
        const establishedCall = Array.from(this.activeCalls.values()).find(c => c.status !== 'ringing');
        return establishedCall ? establishedCall.id : null;
    }

    extractDomain(wsUri) {
        try {
            return new URL(wsUri).hostname;
        } catch (e) {
            const match = wsUri.match(/wss?:\/\/([^:\/]+)/);
            return match ? match[1] : 'localhost';
        }
    }

    getCallDuration(callObj) {
        if (!callObj.connectTime) return '00:00';
        const duration = Math.floor((Date.now() - callObj.connectTime) / 1000);
        const hours = Math.floor(duration / 3600);
        const minutes = Math.floor((duration % 3600) / 60);
        const seconds = duration % 60;

        if (hours > 0) {
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    getStatus() {
        return {
            isEnabled: this.isEnabled,
            isRegistered: this.isRegistered,
            isMinimized: this.isMinimized,
            activeCalls: this.activeCalls.size,
            currentCallId: this.currentCallId,
            reconnectAttempts: this.reconnectAttempts
        };
    }

    getActiveCalls() {
        return Array.from(this.activeCalls.values()).map(call => ({
            id: call.id,
            number: call.number,
            direction: call.direction,
            status: call.status,
            duration: this.getCallDuration(call),
            isHeld: call.isHeld,
            isMuted: call.isMuted
        }));
    }

    enable() { if (!this.isEnabled) this.togglePhone(); }
    disable() { if (this.isEnabled) this.togglePhone(); }
    minimize() { if (!this.isMinimized) this.minimizePhone(); }
    restore() { if (this.isMinimized) this.restorePhone(); }

    // Call control API
    hold(callId = this.currentCallId) { if (callId) this.holdCall(callId); }
    unhold(callId = this.currentCallId) { if (callId) this.unholdCall(callId); }
    mute(callId = this.currentCallId) { if (callId) this.muteCall(callId); }
    unmute(callId = this.currentCallId) { if (callId) this.unmuteCall(callId); }
    hangup(callId = this.currentCallId) { if (callId) this.hangupCall(callId); }
    call(number) { return this.makeCall(number); }


    destroy() {
        if (this.isDestroyed) return;
        this.isDestroyed = true;
        if (this.timerInterval) clearInterval(this.timerInterval);
        this.stopReconnectMonitoring();
        const hangupPromises = Array.from(this.activeCalls.keys()).map(id => this.hangupCall(id));
        Promise.all(hangupPromises).then(() => {
            if (this.registerer) this.registerer.unregister().catch(e => this.log.warn(e));
            if (this.userAgent) this.userAgent.stop().catch(e => this.log.warn(e));
        });

        const header = this.container.querySelector('.softphone-header');
        if (header) {
            // The mousedown/touchstart handlers are local to setupDragging, no need to remove here.
        }
        document.removeEventListener('mousemove', this._boundHandleMouseMove);
        document.removeEventListener('mouseup', this._boundHandleMouseUp);
        document.removeEventListener('touchmove', this._boundHandleTouchMove);
        document.removeEventListener('touchend', this._boundHandleTouchEnd);

        this.emit('destroyed');
        super.destroy();
    }

}